import { GemSpec, BaseProcess, BaseProcessMetadata, CommonGraph } from '@common/types';
import { ProphecyError } from '@prophecy/utils/error';
import {
  DotProcessesDotPropertyName,
  getCurrentProcessGraphPath,
  getGraphProperty,
  getProcessesPath,
  hasChildProcess,
  isSubGraphOrMetaGem
} from 'frontend/core/src/Parser/bindings';
import { useSearchParams } from 'react-router-dom';

import { Entity, IDEEntity } from '../types/Entity';

type NodeType = BaseProcess<BaseProcessMetadata>;

export const getUpstreamNodes = (graph: CommonGraph, nodeId: string): NodeType[] => {
  const nodes = graph.processes;
  const connections = graph.connections;
  const upstreamConnections = connections.filter((connection) => connection.target === nodeId);
  const nodeSet: Set<NodeType> = new Set();

  upstreamConnections.forEach(({ source }) => {
    nodeSet.add(nodes[source]);
    getUpstreamNodes(graph, source).forEach((node) => nodeSet.add(node));
  });

  return Array.from(nodeSet);
};

export function getProcessesWithPaths(rootGraph: CommonGraph, parentPath: string[] = []) {
  let processes: Record<string, NodeType & { path: string[] }> = {};
  Object.values(rootGraph.processes).forEach((process) => {
    const currentPath = [...parentPath, process.metadata.label];
    processes[process.id] = { ...process, path: currentPath };
    if (hasChildProcess(process)) {
      processes = { ...processes, ...getProcessesWithPaths(process, currentPath) };
    }
  });

  return processes;
}

export function getCurrentGraphId(graphPath: string) {
  return graphPath.split(DotProcessesDotPropertyName).pop();
}

export const useIDEQueryParams = () => {
  const [params] = useSearchParams();
  const udf = params.get('functionName') || '';
  const configInstance = params.get('configInstance') || '';
  return { udf, configInstance };
};

export function toIDEEntity(entity: string): IDEEntity {
  switch (entity) {
    case Entity.Configuration.toLowerCase():
      return Entity.Configuration;
    case Entity.Gem.toLowerCase():
      return Entity.Gem;
    case Entity.Pipeline.toLowerCase():
      return Entity.Pipeline;
    case Entity.Job.toLowerCase():
      return Entity.Job;
    case Entity.Lineage.toLowerCase():
      return Entity.Lineage;
    case Entity.Observation.toLowerCase():
      return Entity.Observation;
    case Entity.Project.toLowerCase():
      return Entity.Project;
    case Entity.Snapshot.toLowerCase():
      return Entity.Snapshot;
    case Entity.Model.toLowerCase():
      return Entity.Model;
    case Entity.Macro.toLowerCase():
      return Entity.Macro;
    case Entity.SingularDataTest.toLowerCase():
      return Entity.SingularDataTest;
    case Entity.GenericDataTest.toLowerCase():
      return Entity.GenericDataTest;
    case Entity.App.toLowerCase():
      return Entity.App;
    default:
      throw new ProphecyError('IDE not support for entity:' + entity);
  }
}

export function getProjectIdFromSubEntityId(id: string) {
  return id.split('/')[0];
}

export function getUpstreamProcessId({
  rootGraph,
  graphPath,
  rootPath,
  portId,
  specs
}: {
  rootGraph: CommonGraph;
  graphPath: string;
  rootPath: string;
  portId: string;
  specs: GemSpec[];
}) {
  const graph = getGraphProperty(rootGraph, graphPath, rootPath) as CommonGraph | undefined;
  const connection = graph?.connections.find((connection) => connection.targetPort === portId);
  if (!graph || !connection) {
    return { upstreamGraphPath: graphPath };
  }

  const { source: upstreamProcessId, sourcePort } = connection;

  // if the upstream process is a subgraph, and the connection is to the input of the subgraph
  // we have to return the process from the upstream node of the subgraph
  if (graph.id === upstreamProcessId) {
    return getUpstreamProcessId({
      rootGraph,
      graphPath: getCurrentProcessGraphPath(upstreamProcessId, graphPath),
      rootPath,
      portId: sourcePort,
      specs
    });

    // else if the upstream process is subgraph and the connection is to the output of the subgraph
    // we have to return the process from the subgraph connecting to that port
  } else if (isSubGraphOrMetaGem(graph.processes[upstreamProcessId], specs)) {
    return getUpstreamProcessId({
      rootGraph,
      graphPath: `${getProcessesPath(graphPath)}.${upstreamProcessId}`,
      rootPath,
      portId: sourcePort,
      specs
    });
  }

  return { upstreamGraphPath: graphPath, upstreamProcessId, upstreamPortId: sourcePort };
}
